// iter 153 - Claim account screen.
// URL: /quiz-claim?session_id={ORIG}[&upgrade_session={UP}]
//
// Last step of the post-payment flow. We already have the user's
// email + tier from Stripe metadata. They set a password to claim
// the account, then we drop them at /relocation/blueprint (intake)
// so they can finish populating their personalised Blueprint.

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, Loader2, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { trackQuizFunnelCompleted } from '../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL || '';

// iter 154 - Final tier → total paid in cents. Source of truth for the
// GA4 quiz_funnel_completed event.
const TIER_TOTAL_CENTS = {
  blueprint_only: 19900,
  essentials:     34900,
  guided:         54900,
};

const QuizClaimAccount = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const sessionId = params.get('session_id') || '';
  const upgradeSession = params.get('upgrade_session') || '';

  const [validating, setValidating] = useState(true);
  const [emailDisplay, setEmailDisplay] = useState('');
  const [tier, setTier] = useState('blueprint_only');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!sessionId) { setValidating(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/tools/checkout/quiz-session/${encodeURIComponent(sessionId)}`);
        const d = await r.json().catch(() => ({}));
        if (!cancelled && r.ok && d.paid && d.flow === 'quiz_lead_direct') {
          setEmailDisplay(d.email || '');
          setTier(d.tier || 'blueprint_only');
        }
      } catch { /* ignored */ } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords don\u2019t match.'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/tools/checkout/quiz-claim`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          upgrade_session: upgradeSession || undefined,
          password,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(d.detail || 'Could not activate your account.');
        setBusy(false);
        return;
      }
      // Persist token client-side too (cookie is already set by the BE).
      if (d.token) {
        try { localStorage.setItem('auth_token', d.token); } catch { /* noop */ }
      }
      // iter 154 - Fire GA4 funnel-completion event before we navigate
      // so it lands before the page unloads.
      const finalTier = d.tier || tier || 'blueprint_only';
      try {
        trackQuizFunnelCompleted({
          tier: finalTier,
          upgraded_from_blueprint_only: !!upgradeSession,
          total_paid_cents: TIER_TOTAL_CENTS[finalTier] ?? TIER_TOTAL_CENTS.blueprint_only,
        });
      } catch { /* noop */ }
      // Reload the AuthContext user so /relocation/blueprint sees them.
      try { await checkAuth?.(); } catch { /* noop */ }
      navigate('/relocation/blueprint?paid=1', { replace: true });
    } catch {
      setError('Network issue. Please try again.');
      setBusy(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-[#F9F6EF] flex items-center justify-center">
        <Loader2 className="w-7 h-7 text-[#1A5C6E] animate-spin" />
      </div>
    );
  }
  if (!sessionId || !emailDisplay) {
    return (
      <div className="min-h-screen bg-[#F9F6EF] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border border-[#EFE6CF] rounded-2xl p-8 text-center">
          <h1 className="font-display italic text-2xl text-[#0A1F2E] mb-3">
            We couldn&rsquo;t verify your payment.
          </h1>
          <p className="text-sm text-[#0A1F2E]/70 mb-5">
            If you just paid, please reload in a few seconds.
          </p>
        </div>
      </div>
    );
  }

  const tierLabel = tier === 'guided' ? 'Guided'
                  : tier === 'essentials' ? 'Essentials'
                  : 'Blueprint Only';

  return (
    <div className="min-h-screen bg-[#F9F6EF] py-12 md:py-20 px-6" data-testid="quiz-claim-page">
      <div className="max-w-md mx-auto">
        <div className="flex items-center justify-center gap-2 mb-6">
          <CheckCircle2 className="w-5 h-5 text-[#2CA8A0]" />
          <p className="font-mono uppercase text-[10px] tracking-[0.22em] text-[#1A5C6E]">
            Payment confirmed · {tierLabel}
          </p>
        </div>
        <header className="text-center mb-8">
          <h1
            className="font-display italic text-3xl md:text-4xl text-[#0A1F2E] leading-tight mb-2"
            data-testid="quiz-claim-h1"
          >
            Create your password
          </h1>
          <p className="text-sm text-[#0A1F2E]/70">
            One quick step to claim your account.
          </p>
        </header>
        <form
          onSubmit={handleSubmit}
          className="bg-white border border-[#EFE6CF] rounded-2xl p-6 md:p-7 space-y-4"
          data-testid="quiz-claim-form"
        >
          <div>
            <label className="block font-mono uppercase text-[10px] tracking-[0.18em] text-[#0A1F2E]/55 mb-1.5">
              Email
            </label>
            <input
              type="email"
              value={emailDisplay}
              readOnly
              data-testid="quiz-claim-email"
              className="w-full px-3 py-2.5 bg-[#F9F6EF] border border-[#EFE6CF] rounded-lg text-sm text-[#0A1F2E]/75"
            />
          </div>
          <div>
            <label className="block font-mono uppercase text-[10px] tracking-[0.18em] text-[#0A1F2E]/55 mb-1.5">
              New password
            </label>
            <div className="relative">
              <input
                type={show ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                autoFocus
                data-testid="quiz-claim-password"
                className="w-full px-3 py-2.5 pr-10 bg-white border border-[#EFE6CF] rounded-lg text-sm focus:outline-none focus:border-[#2CA8A0]"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                aria-label={show ? 'Hide password' : 'Show password'}
                className="absolute inset-y-0 right-0 px-3 flex items-center text-[#0A1F2E]/55 hover:text-[#0A1F2E]"
                tabIndex={-1}
              >
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block font-mono uppercase text-[10px] tracking-[0.18em] text-[#0A1F2E]/55 mb-1.5">
              Confirm password
            </label>
            <input
              type={show ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Type it again"
              data-testid="quiz-claim-confirm"
              className="w-full px-3 py-2.5 bg-white border border-[#EFE6CF] rounded-lg text-sm focus:outline-none focus:border-[#2CA8A0]"
            />
          </div>
          {error && (
            <p className="text-sm text-red-600" data-testid="quiz-claim-error">{error}</p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="quiz-claim-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#E8A830] hover:brightness-95 disabled:opacity-70 disabled:cursor-wait text-[#0A1F2E] font-semibold py-3 rounded-full transition-colors"
          >
            {busy ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Activating…</>
            ) : (
              <><Lock className="w-4 h-4" /> Create Account and Go to Portal</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default QuizClaimAccount;
